import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { BaseTable, TableColumn } from '../../common/Table';
import type { TableItem } from '../../common/Table/Table.hooks';
import { ActionButton } from '../../common/Table/Table.styles';
import { VisibilityOutlined as VisibilityIcon } from '@mui/icons-material';
import type { SoldItemListRow, SoldItemSortField } from '../../../services/soldItems.service';
import { formatCurrency } from '../../../utils/formatters/money';
import { formatCommercialSaleDate } from '../../../utils/sales/commercial-sale-date';
import { getSaleInvoiceLabel } from '../../../utils/sales/sale-invoice.utils';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../utils/sales/branch-receipt-numbering';
import { useRelatedEntityNavigation } from '../../../hooks/useRelatedEntityNavigation';
import { OrderCodeButton, TotalText } from '../orders/OrdersTable/OrdersTable.styles';

/** BaseTable exige `id`; el API usa `salesOrderItemId`. */
type SoldItemTableRow = SoldItemListRow & TableItem;

interface SalesByPieceTableProps {
  data: SoldItemListRow[];
  onView: (row: SoldItemListRow) => void;
  /** Server-side sort (whole list, not just the visible page). */
  sortBy: SoldItemSortField;
  sortOrder: 'asc' | 'desc';
  onRequestSort: (sortField: string) => void;
}

const colWidth = (
  px: number,
): Pick<TableColumn<SoldItemTableRow>, 'headerStyle' | 'cellStyle'> => ({
  headerStyle: { minWidth: px },
  cellStyle: { minWidth: px },
});

const SalesByPieceTable: React.FC<SalesByPieceTableProps> = ({
  data,
  onView,
  sortBy,
  sortOrder,
  onRequestSort,
}) => {
  const { navigateToRelatedEntity } = useRelatedEntityNavigation();

  const tableRows: SoldItemTableRow[] = data.map((row) => ({
    ...row,
    id: row.salesOrderItemId,
  }));

  const columns: TableColumn<SoldItemTableRow>[] = [
    {
      id: 'productCodeUnique',
      label: 'Código',
      sortField: 'productCodeUnique',
      ...colWidth(140),
    },
    {
      id: 'categoryName',
      label: 'Tipo',
      sortField: 'categoryName',
      ...colWidth(120),
    },
    {
      id: 'orderCode',
      label: SALES_ORDER_NUMBER_LABEL,
      sortField: 'orderCode',
      ...colWidth(130),
      render: (row) => (
        <OrderCodeButton
          component="button"
          type="button"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            navigateToRelatedEntity('SALES_ORDER', row.orderId, row.orderCode);
          }}
        >
          {row.orderCode}
        </OrderCodeButton>
      ),
    },
    ...(BRANCH_RECEIPT_NUMBERING_ENABLED
      ? [
          {
            id: 'branchReceiptCode',
            label: BRANCH_RECEIPT_NUMBER_LABEL,
            sortField: 'branchReceiptCode',
            ...colWidth(130),
            render: (row: SoldItemTableRow) => row.branchReceiptCode ?? '—',
          } as TableColumn<SoldItemTableRow>,
        ]
      : []),
    {
      id: 'saleDate',
      label: 'Fecha de Venta',
      sortField: 'saleDate',
      render: (row) =>
        formatCommercialSaleDate(
          {
            saleDate: row.saleDate,
            createdAt: row.saleDate,
          },
          'es-ES',
          { includeTime: true },
        ),
      ...colWidth(150),
    },
    {
      id: 'customer',
      label: 'Cliente',
      sortField: 'customerName',
      render: (row) => (row.hasCustomer ? (row.customerName ?? 'Sí') : 'No'),
      ...colWidth(120),
    },
    {
      id: 'hasInvoice',
      label: 'Factura',
      sortField: 'hasInvoice',
      render: (row) => getSaleInvoiceLabel(row.hasInvoice),
      ...colWidth(110),
    },
    {
      id: 'finalPrice',
      label: 'Precio de Venta',
      sortField: 'finalPrice',
      render: (row) => <TotalText variant="body2">{formatCurrency(row.finalPrice)}</TotalText>,
      align: 'right',
      ...colWidth(120),
    },
    {
      id: 'sellerDisplayName',
      label: 'Vendedor',
      sortField: 'sellerName',
      ...colWidth(130),
    },
    {
      id: 'locationName',
      label: 'Ubicación',
      sortField: 'locationName',
      ...colWidth(120),
    },
    {
      id: 'daysUntilSale',
      label: 'Días hasta la venta',
      sortField: 'daysUntilSale',
      render: (row) => row.daysUntilSale,
      align: 'right',
      ...colWidth(130),
    },
  ];

  return (
    <BaseTable
      columns={columns}
      items={tableRows}
      getItemId={(row) => row.salesOrderItemId}
      cellTone="muted"
      showActions
      emptyStateTitle="No hay piezas vendidas con estos filtros."
      sortBy={sortBy}
      sortOrder={sortOrder}
      onRequestSort={onRequestSort}
      renderActions={(row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Ver detalle" placement="top" arrow>
            <ActionButton
              size="small"
              onClick={() => onView(row)}
              aria-label={`Ver detalle de ${row.productCodeUnique}`}
            >
              <VisibilityIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
        </Box>
      )}
    />
  );
};

export default SalesByPieceTable;
